<script setup lang="ts">
    import { Jollywhere } from '@/constants';
    import { BettingData } from '@/models/bettingData';
    import { useAppStore } from '@/stores/app';
    import AppGrid from '@/pages/core/grid/AppGrid.vue';
    import { GameGridItem } from '@/models/gameGrid';
    import { AppError } from '@/errors/appError';
    import { calculatePosition } from '@/helpers';
    import { computed, watch } from 'vue';
    import { BetItem } from '@/models/bet';
    import { useNumberFormatter } from '@/composables/useNumberFormatter';

    const { id, isConfirm = false } = defineProps<{
        id: string;
        isConfirm?: boolean;
    }>();

    const appStore = useAppStore();
    const bettingData = computed<BettingData>(() => appStore.betFeedData[id]);
    const { formatNumber } = useNumberFormatter();

    const bets = computed<BetItem[]>(() => {
        return appStore.getBetsByParams({ customEventId: bettingData.value.customEventId, periodTypeId: bettingData.value.periodTypeId });
    });

    const position: Ref<null | string> = ref(null);

    watch(
        () => bets.value,
        () => {
            const totalBetBalance = calculatePosition(bettingData.value.customEventId, bettingData.value.periodTypeId, bettingData.value.eventScoreForBet);

            if (totalBetBalance !== null) {
                position.value = totalBetBalance.toFixed(2);
            }
        },
        { immediate: true }
    );

    let gameGridItem: GameGridItem;
    const isShowGameGrid = ref<boolean>(false);

    function showGameGrid() {
        if (position === null) {
            throw new AppError(`Empty position for game grid`);
        }

        gameGridItem = new GameGridItem({
            periodTypeId: bettingData.value.periodTypeId,
            customEventId: bettingData.value.customEventId,
            iso3: bettingData.value.iso3,
            leagueName: bettingData.value.leagueName,
            teamHomeName: bettingData.value.teamHomeName,
            teamAwayName: bettingData.value.teamAwayName,
            teamHomeLogo: bettingData.value.teamHomeLogo,
            teamAwayLogo: bettingData.value.teamAwayLogo,
            cornersHome: bettingData.value.cornersHome,
            cornersAway: bettingData.value.cornersAway,
            scoreHome: bettingData.value.scoreHome,
            scoreAway: bettingData.value.scoreAway,
            jollywhere: bettingData.value.jollywhere,
            time: bettingData.value.time,
            eventFeedType: bettingData.value.eventFeedType,
        });
        isShowGameGrid.value = true;
    }
</script>

<template>
    <v-container fluid class="pa-0 ma-0">
        <v-row class="pa-0 ma-0" style="height: 15px">
            <v-col class="pa-0 ma-0">
                <span class="popup-header font14 font-weight-regular bet-dialog-event-line mw-event-line" :class="[isConfirm ? 'bet-dialog-confirm' : '', position !== null ? 'position' : '']">
                    <span :class="[bettingData.handicap == '0.0' ? 'dogTeam' : bettingData.jollywhere === Jollywhere.Home ? 'jollywhereTeam' : 'dogTeam']">
                        {{ bettingData.teamHomeName }}
                    </span>
                    vs
                    <span :class="[bettingData.handicap == '0.0' ? 'dogTeam' : bettingData.jollywhere === Jollywhere.Away ? 'jollywhereTeam' : 'dogTeam']">
                        {{ bettingData.teamAwayName }}
                    </span>
                </span>
            </v-col>
            <v-col v-if="position !== null" class="pa-0 ma-0 d-flex flex-column align-center justify-center" style="min-width: 65px; max-width: 65px">
                <v-chip :color="Number(position) >= 0 ? 'green' : 'red'" class="short grid-h24" size="small" variant="outlined" @click.stop="showGameGrid()">
                    <span class="flex-1 text-center-nowrap">
                        <span v-if="Number(position) > 0">+</span>
                        <span>{{ formatNumber(position, 'money') }}</span>
                    </span>
                </v-chip>
            </v-col>
        </v-row>
    </v-container>

    <AppGrid v-model="isShowGameGrid" :gameGridItem="gameGridItem"></AppGrid>
</template>
